import React from 'react';

import classNames from 'classnames';

import { money, percent, ZERO_DASH } from './formatting';

import WarningIcon from 'Assets/icons/exclamation-triangle.svg';

export const tdClass = 'tw-border-b tw-border-grey tw-p-0 tw-text-end tw-align-middle tw-whitespace-nowrap';

/** A figure with an optional small line underneath (a share, or a status). */
export function Figure({ value, sub, className, subClassName, dash = false }) {
    return (
        <div
            className={classNames(
                'tw-flex tw-min-h-[38px] tw-flex-col tw-items-end tw-justify-center tw-gap-0.5 tw-px-3 tw-py-1.5 tw-leading-tight',
                className,
            )}
        >
            <span className={classNames({ 'tw-text-charcoal-25': dash })}>{value}</span>
            {sub !== undefined && sub !== null && sub !== '' && <span className={classNames('tw-text-2xs tw-text-charcoal-50', subClassName)}>{sub}</span>}
        </div>
    );
}

/**
 * Unallocated: amber while budget is left, a muted "–" when nothing is left, and red only when
 * a line is allocated past 100% — "X over" on that line, and on a department, cost centre
 * or project holding it either "X over" or, when the row still has budget left, the
 * number of lines over.
 */
export function UnallocatedFigure({ figures, showShare = true }) {
    const isLine = figures.lineCount === 1 && figures.pctSum !== undefined;

    if (figures.over && !isLine && figures.unallocated > 0.5) {
        return (
            <Figure
                className="tw-font-medium tw-text-amber-700"
                value={money(figures.unallocated)}
                sub={
                    <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-font-medium tw-text-red">
                        <WarningIcon className="tw-h-3 tw-w-3" aria-hidden="true" />
                        {figures.overCount === 1 ? $t('1 line over 100%') : $t('{{count}} lines over 100%', { count: figures.overCount })}
                    </span>
                }
            />
        );
    }

    if (figures.over) {
        return (
            <Figure
                className="tw-font-medium tw-text-red"
                value={Math.abs(figures.unallocated) < 0.5 ? ZERO_DASH : $t('{{amount}} over', { amount: money(Math.abs(figures.unallocated)) })}
                sub={
                    <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-font-medium tw-text-red">
                        <WarningIcon className="tw-h-3 tw-w-3" aria-hidden="true" />
                        {$t('{{pct}} allocated', { pct: percent(figures.totalPct) })}
                    </span>
                }
            />
        );
    }

    const settled = figures.unallocated <= 0.5;
    // Allocated past the budget with no line over (a negative project balance beneath): "–", no share.
    const share = showShare && figures.budget > 0 && figures.unallocated > -0.5 ? percent(Math.max(0, (figures.unallocated / figures.budget) * 100)) : null;

    return (
        <Figure
            className={settled ? 'tw-font-light tw-text-charcoal-25' : 'tw-font-medium tw-text-amber-700'}
            value={settled ? ZERO_DASH : money(figures.unallocated)}
            sub={share}
        />
    );
}

/** Allocated across every column, with its share of the row. */
export function AllocatedFigure({ figures }) {
    if (!figures.any) {
        return <Figure value={ZERO_DASH} dash />;
    }

    return <Figure className="tw-font-medium" value={money(figures.allocated)} sub={figures.totalPct === null ? null : percent(figures.totalPct)} />;
}
